'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

import { financialDataService, type MarketIndex } from '@/lib/services/financialDataService';
import { FinancialAPIAdapter } from '@/lib/services/financialAPIAdapter';

// Symbol mapping for major Indian indices
const INDEX_SYMBOLS = [
  { symbol: '^NSEI', name: 'NIFTY 50', id: 'nifty50' },
  { symbol: '^BSESN', name: 'SENSEX', id: 'sensex' },
  { symbol: '^NSEBANK', name: 'BANK NIFTY', id: 'banknifty' },
];

export default function MarketOverview() {
  const [indices, setIndices] = useState<MarketIndex[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataSource, setDataSource] = useState<'yahoofinance' | 'supabase'>('yahoofinance');

  const fetchFromYahooFinance = async (): Promise<MarketIndex[] | null> => {
    try {
      const symbols = INDEX_SYMBOLS.map((index) => index.symbol);
      
      // Skip Finnhub for Indian indices - it doesn't support Yahoo Finance symbol formats
      // Use Alpha Vantage directly which supports ^NSEI, ^BSESN, etc.
      console.log('Fetching Indian market indices from Alpha Vantage...');
      const adapter = new FinancialAPIAdapter('alphavantage');
      const quotes = await adapter.getMultipleQuotes(symbols);

      if (quotes.size === 0) {
        console.log('Alpha Vantage returned no data');
        return null;
      }

      const indicesData: MarketIndex[] = [];
      INDEX_SYMBOLS.forEach((index) => {
        const quote = quotes.get(index.symbol);
        if (quote) {
          indicesData.push({
            id: index.id,
            name: index.name,
            symbol: index.symbol,
            value: quote.price,
            change: quote.change,
            changePercent: quote.changePercent,
            high: quote.high,
            low: quote.low,
            category: 'index',
            updatedAt: new Date().toISOString(),
          });
        }
      });

      return indicesData.length > 0 ? indicesData : null;
    } catch (err) {
      console.error('Financial API error:', err);
      return null;
    }
  };

  const fetchFromSupabase = async (): Promise<MarketIndex[]> => {
    try {
      return await financialDataService.getMarketIndices();
    } catch (err) {
      console.error('Supabase fallback error:', err);
      return [];
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Try Financial API first (Finnhub with Alpha Vantage fallback)
      const apiData = await fetchFromYahooFinance();
      
      if (apiData && apiData.length > 0) {
        setIndices(apiData);
        setDataSource('yahoofinance');
        setError(null);
      } else {
        // Fallback to Supabase
        console.log('Falling back to Supabase data');
        const supabaseData = await fetchFromSupabase();
        setIndices(supabaseData);
        setDataSource('supabase');
        setError(null);
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to load market data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Auto-refresh every 30 seconds
    const refreshInterval = setInterval(() => {
      fetchData();
    }, 30000);

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  if (loading) {
    return (
      <section className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-muted-foreground">Loading market data...</div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="container mx-auto px-4 py-16 max-w-7xl">
        <div className="flex items-center justify-center py-12">
          <div className="text-secondary">Error: {error}</div>
        </div>
      </section>
    );
  }

  return (
    <section className="container mx-auto px-4 py-16 max-w-7xl">
      <div className="flex items-center justify-between mb-8 border-[rgba(121,119,119,1)]">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-2 font-display text-[rgba(97,97,97,1)]">
            Market Overview
          </h2>
          <p className="text-muted-foreground">
            Live indices updated every 30 seconds {dataSource === 'yahoofinance' ? '(Yahoo Finance API)' : '(Supabase Fallback)'}
          </p>
        </div>
        <Link
          href="/markets"
          className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors font-medium"
        >
          View All
          <Icon name="ArrowRightIcon" size={20} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {indices?.map((index) => (
          <Link
            key={index.id}
            href={`/asset/${index.symbol}`}
            className="bg-card border border-border rounded-xl p-6 hover:border-primary transition-all hover:scale-105 block"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-foreground">{index.name}</h3>
              <div className="w-2 h-2 bg-primary rounded-full pulse-glow" />
            </div>

            <p className="text-3xl font-bold text-foreground font-mono mb-2">
              {(index.value ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>

            <div
              className={`flex items-center gap-2 mb-4 ${
                (index.change ?? 0) >= 0 ? 'text-primary' : 'text-secondary'
              }`}
            >
              <Icon
                name={(index.change ?? 0) >= 0 ? 'ArrowUpIcon' : 'ArrowDownIcon'}
                size={20}
                variant="solid"
              />
              <span className="text-lg font-semibold font-mono">
                {Math.abs(index.change ?? 0).toFixed(2)} ({Math.abs(index.changePercent ?? 0).toFixed(2)}%)
              </span>
            </div>

            <div className="flex items-center justify-between text-sm text-muted-foreground pt-4 border-t border-border">
              <div>
                <span className="block text-xs mb-1">High</span>
                <span className="font-mono text-foreground">
                  {(index.high ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
              <div className="text-right">
                <span className="block text-xs mb-1">Low</span>
                <span className="font-mono text-foreground">
                  {(index.low ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}